/* ==========================================================================
   VELIORA — Global utilities: drawers, modals, focus trap, toasts, reveal
   ========================================================================== */
(function () {
  'use strict';

  window.Veliora = window.Veliora || {};

  /* ---------- Focus trap ---------- */
  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  function trapFocus(container, initialFocus) {
    const focusable = getFocusable(container);
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    (initialFocus || first).focus();

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return () => container.removeEventListener('keydown', onKeydown);
  }
  Veliora.trapFocus = trapFocus;

  /* ---------- Drawer system ---------- */
  let activeDrawer = null;
  let releaseFocusTrap = null;
  let lastFocusedEl = null;

  function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    closeAllDrawers();
    lastFocusedEl = document.activeElement;
    drawer.classList.add('is-open');
    const overlay = drawer.parentElement.querySelector('.drawer-overlay') || document.querySelector('[data-drawer-overlay="' + id + '"]') || document.getElementById(id + '-overlay');
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    releaseFocusTrap = trapFocus(drawer);
    activeDrawer = drawer;
    drawer.dispatchEvent(new CustomEvent('drawer:open'));
  }

  function closeAllDrawers() {
    document.querySelectorAll('.drawer.is-open').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.dispatchEvent(new CustomEvent('drawer:close'));
    });
    document.querySelectorAll('.drawer-overlay.is-open').forEach((o) => o.classList.remove('is-open'));
    document.body.classList.remove('drawer-open');
    if (releaseFocusTrap) releaseFocusTrap();
    releaseFocusTrap = null;
    if (lastFocusedEl && document.body.contains(lastFocusedEl)) lastFocusedEl.focus();
    activeDrawer = null;
  }
  Veliora.openDrawer = openDrawer;
  Veliora.closeAllDrawers = closeAllDrawers;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer-overlay')) {
      closeAllDrawers();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllDrawers();
      closeAllModals();
    }
  });

  /* ---------- Modal system ---------- */
  function openModal(id) {
    const modal = document.getElementById(id);
    if (!modal) return;
    lastFocusedEl = document.activeElement;
    modal.classList.add('is-open');
    document.body.classList.add('modal-open');
    releaseFocusTrap = trapFocus(modal.querySelector('.modal-panel') || modal);
    modal.dispatchEvent(new CustomEvent('modal:open'));
  }
  function closeAllModals() {
    document.querySelectorAll('.modal-overlay.is-open').forEach((m) => {
      m.classList.remove('is-open');
      m.dispatchEvent(new CustomEvent('modal:close'));
    });
    document.body.classList.remove('modal-open');
    if (releaseFocusTrap) releaseFocusTrap();
    releaseFocusTrap = null;
    if (lastFocusedEl && document.body.contains(lastFocusedEl)) lastFocusedEl.focus();
  }
  Veliora.openModal = openModal;
  Veliora.closeAllModals = closeAllModals;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      e.preventDefault();
      openModal(opener.getAttribute('data-modal-open'));
      return;
    }
    if (e.target.closest('[data-modal-close]') || e.target.classList.contains('modal-overlay')) {
      closeAllModals();
    }
  });

  /* ---------- Toasts ---------- */
  function toast(message, type) {
    let wrap = document.querySelector('.toast-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'toast-wrap';
      wrap.setAttribute('aria-live', 'polite');
      document.body.appendChild(wrap);
    }
    const el = document.createElement('div');
    el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
    el.textContent = message;
    wrap.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => el.remove(), 300);
    }, 3400);
  }
  Veliora.toast = toast;

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    const items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    items.forEach((el, i) => {
      el.style.transitionDelay = (i % 4) * 80 + 'ms';
      io.observe(el);
    });
  }

  /* ---------- Sticky header show/hide on scroll ---------- */
  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    function update() {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 40);
      if (y > lastY && y > 200) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    });
  }

  /* ---------- Nav mega menu (hover + keyboard) ---------- */
  function initMegaMenu() {
    document.querySelectorAll('.nav-item').forEach((item) => {
      const link = item.querySelector('.site-header__nav-link');
      const menu = item.querySelector('.mega-menu');
      if (!menu || !link) return;
      let closeTimer;
      function open() {
        clearTimeout(closeTimer);
        document.querySelectorAll('.nav-item.is-open').forEach((n) => n !== item && n.classList.remove('is-open'));
        item.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
      }
      function close() {
        closeTimer = setTimeout(() => {
          item.classList.remove('is-open');
          link.setAttribute('aria-expanded', 'false');
        }, 120);
      }
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);
      link.addEventListener('click', (e) => {
        if (menu) {
          e.preventDefault();
          item.classList.contains('is-open') ? close() : open();
        }
      });
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.nav-item')) {
        document.querySelectorAll('.nav-item.is-open').forEach((n) => n.classList.remove('is-open'));
      }
    });
  }

  /* ---------- Mobile nav accordions ---------- */
  function initMobileNav() {
    document.querySelectorAll('.mobile-nav__link[data-toggle-submenu]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const panel = document.getElementById(btn.getAttribute('data-toggle-submenu'));
        const isOpen = panel.classList.contains('is-open');
        btn.setAttribute('aria-expanded', String(!isOpen));
        panel.classList.toggle('is-open', !isOpen);
      });
    });
  }

  /* ---------- Footer accordions ---------- */
  function initAccordions(selectorTrigger) {
    document.querySelectorAll(selectorTrigger).forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!isOpen));
        if (panel) panel.classList.toggle('is-open', !isOpen);
      });
    });
  }

  /* ---------- Counter animation ---------- */
  function initCounters() {
    const counters = document.querySelectorAll('[data-counter-target]');
    if (!counters.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          const target = parseFloat(el.getAttribute('data-counter-target'));
          const suffix = el.getAttribute('data-counter-suffix') || '';
          const decimals = (el.getAttribute('data-counter-target').split('.')[1] || '').length;
          const duration = 1400;
          const start = performance.now();
          function frame(now) {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const value = target * eased;
            el.textContent = value.toFixed(decimals) + suffix;
            if (progress < 1) requestAnimationFrame(frame);
            else el.textContent = target.toFixed(decimals) + suffix;
          }
          requestAnimationFrame(frame);
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((el) => io.observe(el));
  }

  /* ---------- Quantity selectors ---------- */
  function initQuantitySelectors() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      const wrap = btn.closest('.quantity-selector');
      const input = wrap.querySelector('input[type="number"]');
      const step = parseInt(btn.getAttribute('data-qty-step'), 10);
      const min = parseInt(input.min || '1', 10);
      const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Tabs (generic) ---------- */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach((tabGroup) => {
      const buttons = tabGroup.querySelectorAll('[data-tab-btn]');
      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const target = btn.getAttribute('data-tab-btn');
          tabGroup.querySelectorAll('[data-tab-btn]').forEach((b) => b.classList.toggle('is-active', b === btn));
          tabGroup.querySelectorAll('[data-tab-panel]').forEach((p) => {
            p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === target);
          });
        });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeaderScroll();
    initMegaMenu();
    initMobileNav();
    initAccordions('.footer-accordion__trigger');
    initAccordions('.product-accordion-trigger');
    initAccordions('.faq-item__trigger');
    initCounters();
    initQuantitySelectors();
    initTabs();
  });
})();
